import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  Switch,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { TenantBasicDetailModel } from "./TenantModel";
import { CreateTenantBasicDetail } from "../../../services/SuperAdminServices/TenantService";
import { useAppContext } from "../../../context/AppContext";

// this is the model of the ADD the TENANT Basic Details


interface TenantFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  tenant: TenantBasicDetailModel | null;
}

const TenantDetailsForm: React.FC<TenantFormProps> = ({
  isOpen,
  onClose,
  onSave,
  tenant,
}) => {
  const { accessToken } = useAppContext();
  const [formData, setFormData] = useState<TenantBasicDetailModel>({
    Id: 0,
    TenantID: "",
    Name: "",
    Description: "",
    PhoneNumber: "",
    Email: "",
    Address: "",
    IsActive: true,
    IsTrial: false,
    DailyProcessLimitPerUser: 10,
    TeamAppId:""
  });

  const toast = useToast();

  useEffect(() => {
    if (tenant) {
      setFormData(tenant);
    } else {
      setFormData({
        Id: 0,
        TenantID: "",
        Name: "",
        Description: "",
        PhoneNumber: "",
        Email: "",
        Address: "",
        IsActive: true,
        IsTrial: false,
        DailyProcessLimitPerUser: 10,
        TeamAppId:""
      });
    }
  }, [tenant]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = () => {
    setFormData((prev) => ({ ...prev, IsActive: !prev.IsActive }));
  };

  const handleTrialSwitchChange = () => {
    setFormData((prev) => ({ ...prev, IsTrial: !prev.IsTrial }));
  };

  const handleDailyLimitChange = (_: string, value: number) => {
    setFormData((prev) => ({
      ...prev,
      DailyProcessLimitPerUser: Number.isNaN(value) ? 10 : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      
      if (!accessToken) {
        throw new Error("Access token not found.");
      }

      await CreateTenantBasicDetail(formData, accessToken);

      toast({
        title: "Tenant saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSave(); // refresh the tenant list
      onClose(); // close modal
    } catch (error: any) {
      toast({
        title: "Failed to save tenant.",
        description: error.message || "An error occurred.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {formData.Id === 0 ? "Add Tenant" : "Edit Tenant"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={3}>
            <FormLabel>Tenant ID</FormLabel>
            <Input
              name="TenantID"
              value={formData.TenantID}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl mb={3}>
            <FormLabel>Name</FormLabel>
            <Input name="Name" value={formData.Name} onChange={handleChange} />
          </FormControl>
          <FormControl>
            <FormLabel>MSTeams ID</FormLabel>
            <Input
              name="TeamAppId"
              value={formData.TeamAppId}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl mb={3}>
            <FormLabel>Description</FormLabel>
            <Input
              name="Description"
              value={formData.Description}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl mb={3}>
            <FormLabel>Phone Number</FormLabel>
            <Input
              name="PhoneNumber"
              value={formData.PhoneNumber}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl mb={3}>
            <FormLabel>Email</FormLabel>
            <Input
              name="Email"
              value={formData.Email}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl mb={3}>
            <FormLabel>Address</FormLabel>
            <Input
              name="Address"
              value={formData.Address}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl display="flex" alignItems="center" mt={4}>
            <FormLabel htmlFor="isActive" mb="0">
              Status
            </FormLabel>
            <Switch
              id="isActive"
              isChecked={formData.IsActive}
              onChange={handleSwitchChange}
            />
          </FormControl>
          <FormControl display="flex" alignItems="center" mt={4}>
            <FormLabel htmlFor="isTrial" mb="0">
              Trial Account
            </FormLabel>
            <Switch
              id="isTrial"
              isChecked={formData.IsTrial}
              onChange={handleTrialSwitchChange}
            />
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Daily Process Limit Per User (mails and documents each)</FormLabel>
            <NumberInput
              value={formData.DailyProcessLimitPerUser}
              min={1}
              onChange={handleDailyLimitChange}
              isDisabled={!formData.IsTrial}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TenantDetailsForm;
